// src/components/desk/RunningNote.jsx — T38 / B1184: what was pasted, when, and what it did.
//
// OWNER, 2026-09-04: "paste text, parse, save, come back, paste another text for more details,
// parse, enrich." Until this, the earlier text was gone the moment the agent pressed parse.
// It was never lost from the DATABASE — `intake.raw_text` has kept all 272 pastes — so this
// panel writes nothing and assembles nothing: `running_note_v1` (0867) owns the definition and
// this draws what it returns.
//
// ⚠️ ITS OWN FILE ON PURPOSE. The two surfaces that mount it are a ~15,000-line file and a
// ~14,000-line one, both login-gated and both dragging in a map; inline, "one entry per batch"
// would be a claim about code nobody had seen drawn. scripts/render-running-note.mjs mounts the
// VIEW below with fixed props — the fetching wrapper renders nothing under
// renderToStaticMarkup, which runs no effects.
//
// THREE KINDS, and the third one is a measurement rather than a design flourish:
//   paste  — a human pasted a message (244 of 272)
//   action — the Desk logged one of its own manual acts through the same table (28 of 272:
//            '[manual edit via Data Desk] …'). Drawn as one quiet line: rendering it as
//            pasted text is noise wearing a quotation mark, and hiding it would drop
//            "the requirement was closed by hand", which belongs on a timeline.
//   media  — our own uploaded photo or document.
import { useEffect, useState } from 'react'
import { t, tLog } from '../../lib/i18n.js'
import { Icon, IconText } from '../Icon.jsx'
import { getRunningNote } from '../../db/core.js'

const KIND_ICON = { paste: 'clipboard', action: 'settings', media: 'image', note: 'file-text' }

// ⚠️ ELEVEN ROWS SAYING "photo". Found by opening a real property on production: an agent uploads
// a set, so the timeline drew one entry per FILE — eleven identical lines, same minute, no size
// (uploads before 0848 recorded none), burying the single pasted message the note exists to show.
// A batch of photos is ONE act, so it draws as one row. Pastes, actions and typed notes are never
// grouped: each of those is something a person did separately and deliberately.
// Grouping is done HERE and not in SQL on purpose — running_note_v1 stays one row per real thing,
// and how they are presented is the panel's business.
export function groupEntries(entries) {
  const out = []
  for (const e of entries || []) {
    const last = out[out.length - 1]
    const sameMinute = last && Math.abs(new Date(e.at) - new Date(last.at)) < 60000
    if (e.kind === 'media' && last?.kind === 'media' && sameMinute && last.author === e.author) {
      last.count = (last.count || 1) + 1
      // The set's size is the sum of what we know; a member with no size adds nothing rather
      // than dragging the total to zero.
      const b = Number(e.detail?.bytes)
      if (Number.isFinite(b)) last.detail = { ...last.detail, bytes: (Number(last.detail?.bytes) || 0) + b }
      continue
    }
    out.push({ ...e })
  }
  return out
}

function fmtWhen(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}
// Sizes are for a human deciding whether a document is the scan or the thumbnail.
function fmtBytes(n) {
  if (!Number.isFinite(n)) return null
  return n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`
}

/** One entry. Long text collapses to three lines and opens in place — a note that pushes the
 *  form off the screen is closed once and never reopened. */
function Entry({ e }) {
  const [open, setOpen] = useState(false)
  const body = e.body || ''
  const long = body.length > 180
  const bytes = fmtBytes(e.detail?.bytes)
  const isAction = e.kind === 'action'
  return (
    <li className="flex gap-2.5 py-2 border-t border-border first:border-t-0" data-note-entry={e.kind}>
      <span className={`mt-0.5 shrink-0 ${isAction ? 'text-muted-foreground/60' : 'text-muted-foreground'}`}>
        <Icon name={KIND_ICON[e.kind] || 'file-text'} size={13} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
          <span className="tabular-nums">{fmtWhen(e.at)}</span>
          {e.author && <span className="font-medium text-foreground/80">{e.author}</span>}
          {e.kind === 'paste' && e.channel && <span className="opacity-70">{t(`desk_note_channel_${e.channel}`)}</span>}
          {e.count > 1 && <span className="font-medium">{tLog('desk_note_photo_count', { n: String(e.count) })}</span>}
          {bytes && <span className="tabular-nums opacity-70">{bytes}</span>}
          {/* A dismissed parse is not a failure worth a red flag — it is a batch that did not
              reach the record, and an agent looking for "where did that go" needs to see it. */}
          {e.kind === 'paste' && e.committed === false && (
            <span className="text-amber-600 dark:text-amber-400">{t('desk_note_not_saved')}</span>
          )}
        </div>
        {isAction
          ? <p className="text-xs text-muted-foreground italic mt-0.5">{e.detail?.action || t('desk_note_manual_action')}</p>
          : e.kind === 'media' && e.count > 1 ? null
          : (
            <p className={`text-sm mt-0.5 whitespace-pre-wrap break-words ${open || !long ? '' : 'line-clamp-3'}`}>
              {body}
            </p>
          )}
        {long && !isAction && (
          <button type="button" className="text-xs text-primary hover:underline mt-0.5"
            data-note-expand onClick={() => setOpen(v => !v)}>
            {open ? t('desk_note_less') : t('desk_note_more')}
          </button>
        )}
      </div>
    </li>
  )
}

/** The pure view. Everything the panel decides is here; the wrapper below only fetches. */
export function RunningNoteView({ entries, loading, err, cardClass = '' }) {
  // ⚠️ An empty note renders NOTHING — not an empty box, not "no entries yet". Most records
  // were typed in by hand and have no batches at all; a permanent empty card on every one of
  // them is the "affordance with nothing behind it" defect (diagnosis rule 6).
  if (loading) return null
  if (err) {
    return (
      <div className={cardClass}>
        <p className="text-xs text-destructive"><IconText>{'⚠️ ' + err}</IconText></p>
      </div>
    )
  }
  if (!entries?.length) return null
  const rows = groupEntries(entries)
  const pastes = entries.filter(e => e.kind === 'paste').length
  return (
    <div className={cardClass} data-running-note>
      <p className="text-xs font-semibold text-muted-foreground mb-1">
        {tLog('desk_note_heading', { n: String(pastes) })}
      </p>
      {/* Oldest first: a note is read in the order it was written. A reverse-chronological feed
          is for a queue you are draining, which this is not. */}
      <ul className="list-none p-0 m-0">
        {rows.map(e => <Entry key={e.id} e={e} />)}
      </ul>
    </div>
  )
}

/** kind: 'property' | 'requirement'. */
export function RunningNote({ kind, id, refreshKey, cardClass = '' }) {
  const [entries, setEntries] = useState(null)
  const [err, setErr] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!id) return
    let gone = false
    setLoading(true)
    getRunningNote(kind, id)
      .then(d => { if (!gone) { setEntries(d.entries || []); setErr(null) } })
      // A 403 on a client's note is the correct answer for a colleague, not an error to shout
      // about: the panel simply is not theirs to read, so it renders nothing at all.
      .catch(e => { if (!gone) { setEntries([]); setErr(/403/.test(e.message) ? null : e.message) } })
      .finally(() => { if (!gone) setLoading(false) })
    return () => { gone = true }
  }, [kind, id, refreshKey])

  return <RunningNoteView entries={entries} loading={loading} err={err} cardClass={cardClass} />
}
